import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useAuth } from "../../accounts/AuthProvider";
import { useAccountPreferences } from "../../accounts/AccountPreferencesProvider";
import { automaticAuthEmail } from "../../accounts/supabaseConfig";
import { ADMIN_BACKUP_FORMAT, adminRequest } from "../../accounts/adminApi";
import { mainActions } from "../../store";
import InvitationPage from "./InvitationPage";
import { accessRequestLink } from "../../accounts/accessRequests";
import Card from "../UI/Card";
import Button from "../UI/Button";
import classes from "./AccountPage.module.css";

const AccountPage = () => {
  const dispatch = useDispatch();
  const { configured, loading, session, client, error: sessionError, retry } = useAuth();
  const preferences = useAccountPreferences();
  const { pathname, search } = useLocation();
  const reset = pathname === "/account/reset-password";
  const callback = pathname === "/account/callback";
  const linkFailed = new URLSearchParams(search).has("error");
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleteAcknowledged, setDeleteAcknowledged] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const request = useRef(0);
  const deletedAccountId = useRef(null);
  useEffect(() => () => { request.current++; }, []);
  useEffect(() => {
    request.current++;
    setBusy(false); setPassword(""); setConfirmation(""); setDeleteConfirmation("");
    setDeleteAcknowledged(false);
    if (deletedAccountId.current) {
      const justDeleted = !session?.user?.id;
      deletedAccountId.current = null;
      setMessage(justDeleted
        ? "Your account and its saved charts and preferences have been permanently deleted."
        : "");
      setError("");
    } else {
      setMessage(""); setError("");
    }
  }, [mode, pathname, session?.user?.id]);

  const submit = async event => {
    event.preventDefault();
    if (busy || !client) return;
    setError(""); setMessage("");
    if (reset && password !== confirmation) {
      setError("The passwords do not match."); return;
    }
    const version = ++request.current;
    setBusy(true);
    try {
      let result;
      if (reset) result = await client.auth.updateUser({ password });
      else if (mode === "forgot") result = await client.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/account/reset-password`,
      });
      else result = await client.auth.signInWithPassword({ email: email.trim(), password });
      if (version !== request.current) return;
      if (result.error) throw result.error;
      setPassword(""); setConfirmation("");
      setMessage(reset ? "Your password has been updated." : mode === "forgot"
        ? "If an account exists for this address, you will receive a password reset email. Open it in this browser."
        : "Signed in.");
    } catch (failure) {
      if (version !== request.current) return;
      setPassword(""); setConfirmation("");
      setError(failure.code === "invalid_credentials" ? "Email or password was not recognised."
        : failure.code === "email_not_confirmed" ? "Please confirm your email before signing in."
        : failure.status === 429 ? "Too many attempts. Please wait before trying again."
        : "The request could not be completed. Please check your connection and try again.");
    } finally { if (version === request.current) setBusy(false); }
  };

  const signOut = async () => {
    const version = ++request.current;
    setBusy(true); setError(""); setMessage("");
    try {
      const result = await client.auth.signOut({ scope: "local" });
      if (version === request.current && result.error) throw result.error;
    } catch {
      if (version === request.current) setError("Could not sign out. Please try again.");
    } finally { if (version === request.current) setBusy(false); }
  };

  const retryPreferences = () => {
    if (window.confirm("Reloading cloud preferences may replace this browser's current supported practice settings. Continue?")) {
      preferences.retry();
    }
  };

  const saveChartDefaults = () => {
    try {
      dispatch(mainActions.saveCurrentChartDefaults());
      setMessage("Current chart settings are saved as defaults for new charts.");
      setError("");
    } catch {
      setMessage("");
      setError("The current chart settings cannot be saved as defaults. Check the chart settings and try again.");
    }
  };

  const downloadData = async () => {
    if (!session?.access_token || busy) return;
    const version = ++request.current;
    setBusy(true); setError(""); setMessage("");
    try {
      const backup = {
        format: ADMIN_BACKUP_FORMAT,
        schemaVersion: 1,
        exportedAt: new Date().toISOString(),
        account: { authUserId: session.user.id, email: session.user.email ?? "" },
        chartCount: 0,
        charts: [],
        preferences: null,
      };
      let afterId = null;
      const cursors = new Set();
      do {
        const result = await adminRequest(session.access_token, "export-own-page", { afterId });
        if (version !== request.current) return;
        if (!Array.isArray(result.charts) || result.account?.authUserId !== session.user.id ||
            typeof result.account.email !== "string" || !Object.hasOwn(result, "preferences") ||
            !Object.hasOwn(result, "nextAfterId")) {
          throw new Error("The account export response was invalid.");
        }
        backup.charts.push(...result.charts);
        if (!afterId) {
          backup.account.email = result.account.email;
          backup.preferences = result.preferences;
        }
        const nextAfterId = result.nextAfterId;
        if (nextAfterId !== null && (typeof nextAfterId !== "string" || cursors.has(nextAfterId))) {
          throw new Error("The account export could not be paged safely.");
        }
        if (nextAfterId) cursors.add(nextAfterId);
        afterId = nextAfterId;
      } while (afterId);
      backup.chartCount = backup.charts.length;
      const filenameEmail = (backup.account.email || "account").toLowerCase()
        .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "account";
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      try {
        if (version !== request.current) return;
        anchor.href = url;
        anchor.download = `guitar-charts-export-${filenameEmail}-${new Date().toISOString().slice(0, 10)}.json`;
        document.body.append(anchor);
        anchor.click();
        setMessage(`Downloaded your account data, including ${backup.chartCount} saved chart${backup.chartCount === 1 ? "" : "s"} and preferences.`);
      } finally {
        anchor.remove();
        URL.revokeObjectURL(url);
      }
    } catch (failure) {
      if (version === request.current) setError(failure.message || "Your account data could not be exported.");
    } finally {
      if (version === request.current) setBusy(false);
    }
  };

  const deleteOwnAccount = async event => {
    event.preventDefault();
    if (!session?.access_token || busy || !deleteAcknowledged ||
        deleteConfirmation.trim().toLowerCase() !== session.user.email?.toLowerCase()) return;
    const version = ++request.current;
    setBusy(true); setError(""); setMessage("");
    try {
      const result = await adminRequest(session.access_token, "delete-own-account", {
        confirmationEmail: deleteConfirmation,
        confirmDelete: true,
      });
      if (version !== request.current) return;
      if (result.deleted !== true) throw new Error("The account deletion could not be confirmed.");
      deletedAccountId.current = session.user.id;
      setMessage("Your account and its saved charts and preferences have been permanently deleted.");
      const signOutResult = await client.auth.signOut({ scope: "local" });
      if (version !== request.current) return;
      if (signOutResult.error) {
        setError("Your account was deleted, but this browser could not clear its cached sign-in. Close this page or sign out.");
      }
    } catch (failure) {
      if (version === request.current) {
        setError(failure.code === "ADMIN_OUTCOME_UNKNOWN"
          ? "The deletion outcome could not be confirmed. Sign in again to check your account before trying again."
          : failure.message || "Your account could not be deleted.");
      }
    } finally {
      if (version === request.current) setBusy(false);
    }
  };

  const heading = reset ? "Set a new password" : session ? "Your account"
    : mode === "forgot" ? "Reset your password" : "Sign in";
  if (pathname === "/account/invite") return <InvitationPage />;
  if (reset && (new URLSearchParams(search).has("token_hash") || new URLSearchParams(search).has("link_error"))) return <InvitationPage type="recovery" />;
  const requestLink = accessRequestLink();
  const resetRequestLink = accessRequestLink(undefined, true);
  return <Card className={classes.panel}>
    <HelmetProvider><Helmet><title>{heading} | Guitar Charts</title><meta name="robots" content="noindex, nofollow" /></Helmet></HelmetProvider>
    <h1>{heading}</h1>
    {!configured ? <p>Accounts are not available on this version of the site yet.</p>
      : loading ? <p role="status">Checking your account…</p>
      : sessionError ? <><p role="alert">{sessionError}</p><Button onClick={retry}>Retry account connection</Button></>
      : <>
        {(error || linkFailed) && <p role="alert">{error || "This email link is invalid or expired. Please request a new one."}</p>}
        {message && <p role="status">{message}</p>}
        {reset && !session ? <><p>Open the password reset link emailed to you to choose a new password.</p>
          <Link to="/account" onClick={() => setMode("forgot")}>Request a new reset link</Link></>
          : session && !reset ? <>
            <p><Link to="/my-charts">My Charts</Link></p>
            <p><Link to="/admin">Administration</Link></p>
            <p className={classes.email}>Signed in as {session.user.email}.</p>
            <p>Chart defaults are saved separately and apply to the next new chart.</p>
            <Button disabled={preferences.status === "error"} onClick={saveChartDefaults}>
              Save current chart settings as account defaults
            </Button>
            {preferences.status === "loading" && <p role="status">Loading cloud preferences…</p>}
            {preferences.status === "syncing" && <p role="status">Syncing preferences…</p>}
            {preferences.status === "synced" && <p role="status">Preferences are synced to your account.</p>}
            {preferences.status === "error" && <>
              <p role="alert">{preferences.error}</p>
              <Button onClick={retryPreferences}>Reconnect preference sync</Button>
            </>}
            <h2>Your data</h2>
            <p>Download a JSON export of your saved charts and cloud preferences.</p>
            <Button disabled={busy} onClick={downloadData}>
              {busy ? "Please wait…" : "Download my data"}
            </Button>
            <section className={classes.danger} aria-labelledby="delete-account-heading">
              <h2 id="delete-account-heading">Delete your account</h2>
              <p>This permanently deletes your account, saved charts, and cloud preferences. This cannot be undone.</p>
              <p>If this is the site owner account, deletion also removes owner administration access.</p>
              <form className={classes.form} onSubmit={deleteOwnAccount}>
                <label htmlFor="delete-account-email">Type your email address to confirm</label>
                <input id="delete-account-email" type="email" autoComplete="off" required disabled={busy}
                  value={deleteConfirmation} onChange={event => setDeleteConfirmation(event.target.value)} />
                <label>
                  <input type="checkbox" checked={deleteAcknowledged} disabled={busy}
                    onChange={event => setDeleteAcknowledged(event.target.checked)} />
                  I understand this permanently deletes my account and private data.
                </label>
                <Button type="submit" disabled={busy || !deleteAcknowledged ||
                  deleteConfirmation.trim().toLowerCase() !== session.user.email?.toLowerCase()}>
                  {busy ? "Please wait…" : "Permanently delete my account"}
                </Button>
              </form>
            </section>
            {callback && <p>Your email link has been accepted.</p>}
            <Button disabled={busy} onClick={signOut}>{busy ? "Signing out…" : "Sign out"}</Button>
          </> : <>
            {!reset && mode === "forgot" && !automaticAuthEmail ? <>
              <p>Password resets are handled personally. Email a request and you will receive a link to choose a new password.</p>
              {resetRequestLink && <p><a href={resetRequestLink}>Request password reset</a></p>}
            </> : <form className={classes.form} onSubmit={submit} aria-busy={busy}>
              {!reset && <><label htmlFor="account-email">Email</label>
                <input id="account-email" type="email" autoComplete="email" value={email}
                  required disabled={busy} onChange={event => setEmail(event.target.value)} /></>}
              {(reset || mode !== "forgot") && <><label htmlFor="account-password">{reset ? "New password" : "Password"}</label>
                <input id="account-password" type="password" autoComplete={reset ? "new-password" : "current-password"}
                  minLength={reset ? 8 : undefined} required disabled={busy}
                  value={password} onChange={event => setPassword(event.target.value)} /></>}
              {reset && <><label htmlFor="account-confirmation">Confirm password</label>
                <input id="account-confirmation" type="password" autoComplete="new-password" minLength={8} required disabled={busy}
                  value={confirmation} onChange={event => setConfirmation(event.target.value)} />
                <p>Use at least 8 characters.</p></>}
              <Button type="submit" disabled={busy || !client}>{busy ? "Please wait…" : reset ? "Update password"
                : mode === "forgot" ? "Send reset email" : "Sign in"}</Button>
            </form>}
            {!reset && <p>Accounts are invitation-only.{!requestLink && " Access requests will be available soon."}</p>}
            {!reset && <div className={classes.actions}>
              {mode !== "signin" && <button disabled={busy} onClick={() => setMode("signin")}>Back to sign in</button>}
              {mode === "signin" && <>{requestLink && <a href={requestLink}>Request access</a>}
                <button disabled={busy} onClick={() => setMode("forgot")}>Forgot password?</button></>}
            </div>}
          </>}
      </>}
    <p><Link to="/">Return to your chart</Link></p>
  </Card>;
};

export default AccountPage;
